import React, { useCallback, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import TablePagination from '@mui/material/TablePagination';
import Tooltip from '@mui/material/Tooltip';
import SearchIcon from '@mui/icons-material/Search';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';

import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RestoreFromTrashIcon from '@mui/icons-material/RestoreFromTrash';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import ContentCutIcon from '@mui/icons-material/ContentCut';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';

import VideoEditingService, {
  BrollDisqualifier, ClipLabelEdit, CREATION_SOURCE_LABEL, CreationSource,
  LibraryClipLabel, LibraryMode,
} from 'services/VideoEditingService';
import { defaultBrollName, formatDuration } from 'pages/VideoEditing/format';
import ClipEditDialog, { ClipEditSave } from './ClipEditDialog';
import BrollFilterBar, { humanize } from './BrollFilterBar';
import AddClipsTab from './AddClipsTab';
import { useBrollSearch } from './useBrollSearch';

type BrollTab = 'library' | 'add';

const videoEditingService = new VideoEditingService();

// Why a clip can't be reused as b-roll, in the words an editor would use.
const DISQUALIFIER_LABEL: Record<BrollDisqualifier, string> = {
  talking: 'talking',
  too_much_sound: 'too loud',
  split: 'split into sub-clips',
  removed: 'removed',
};
const DISQUALIFIER_TOOLTIP: Record<BrollDisqualifier, string> = {
  talking: "Someone talks to camera, so it carries its own context and can't be cut under a different voiceover.",
  too_much_sound: 'More than half the clip has sound, so it fights any voiceover laid over it.',
  split: 'This whole take was split into sub-clips — those are listed separately, so the full take is held back to avoid showing the same footage twice.',
  removed: 'Removed from the library. It still exists on Drive and can be restored.',
};

// Where a clip came from, in a sentence. A map rather than the nested ternary this
// replaced: that one read "not ai_label and not video_editor" as library_trim, so the
// moment a fourth source existed every one of them claimed to have been trimmed here.
const CREATION_SOURCE_TOOLTIP: Record<CreationSource, string> = {
  ai_label: 'Found and labelled by the clip labeler.',
  video_editor: 'Added to the library from the Video Editing tool.',
  library_trim: 'Trimmed out of another clip here in the library.',
  library_import: 'Added here from a Drive folder, in the Add clips tab.',
};

// Kept in step with the sizes the API accepts; anything else falls back server-side.
const PAGE_SIZES = [10, 25, 50];

const CONFIDENCE_COLOR: Record<string, string> = {
  high: '#059669',
  medium: '#0284C7',
  low: '#D97706',
  none: '#64748B',
};

// One result. Shows a still from the label's in-point until it's clicked, then
// plays inline — a grid of 200 videos loading at once would hammer the byte proxy,
// and a still tells you what the footage is at a glance.
function ResultCard({
  label, busy, categoryName, onEdit, onRemove, onRestore,
}: {
  label: LibraryClipLabel;
  busy: boolean;
  categoryName: (slug: string) => string;
  onEdit: (label: LibraryClipLabel) => void;
  onRemove: (label: LibraryClipLabel) => void;
  onRestore: (label: LibraryClipLabel) => void;
}) {
  // The URL is captured when the card is first played, NOT read from the label on
  // every render. Signed stream URLs are re-minted on each search, so reading the
  // live prop would hand <video autoPlay> a new src after any refresh and restart
  // playback on cards nobody touched.
  const [playingSrc, setPlayingSrc] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  function play() {
    if (playingSrc) return;
    setPlayingSrc(label.inSec != null
      ? `${label.streamUrl}#t=${label.inSec},${label.outSec ?? ''}`
      : label.streamUrl);
  }
  // Keyed on having a range, not on scope: a whole take is retrimmable, so a `clip`-scope row
  // can carry one too.
  const range = label.inSec != null && label.outSec != null
    ? `${formatDuration(label.inSec)}–${formatDuration(label.outSec)}`
    : null;
  const confidence = label.productConfidence || 'none';

  return (
    <Paper
      variant="outlined"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        borderLeft: label.scope === 'segment' ? '3px solid #C7D2FE' : '3px solid #6366F1',
      }}
    >
      <Box
        sx={{
          position: 'relative', backgroundColor: '#0F172A', aspectRatio: '9 / 16',
          maxHeight: 260, display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer',
        }}
        onClick={play}
      >
        {playingSrc ? (
          <video
            src={playingSrc}
            controls
            autoPlay
            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
          />
        ) : (
          <>
            {label.posterUrl ? (
              <img
                src={label.posterUrl}
                alt={label.description || label.fileName || 'clip preview'}
                loading="lazy"
                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
              />
            ) : (
              <Typography variant="caption" sx={{ color: '#94A3B8' }}>no preview yet</Typography>
            )}
            {/* Play affordance over the still, so it's clear the card is playable. */}
            <Box
              sx={{
                position: 'absolute', inset: 0, display: 'flex', alignItems: 'center',
                justifyContent: 'center', color: '#F8FAFC', fontSize: 30, opacity: 0.75,
                textShadow: '0 1px 6px rgba(0,0,0,0.6)', pointerEvents: 'none',
              }}
            >
              ▶
            </Box>
          </>
        )}
        {range && (
          <Chip
            size="small"
            label={range}
            sx={{ position: 'absolute', top: 6, left: 6, height: 20, fontSize: 11, backgroundColor: 'rgba(15,23,42,0.75)', color: '#E2E8F0' }}
          />
        )}
        {label.soundRatio != null && (
          <Tooltip title={`${Math.round(label.soundRatio * 100)}% of this clip has audible sound`}>
            <Chip
              size="small"
              icon={<VolumeUpIcon sx={{ fontSize: 12, color: '#E2E8F0 !important' }} />}
              label={`${Math.round(label.soundRatio * 100)}%`}
              sx={{
                position: 'absolute', bottom: 6, left: 6, height: 20, fontSize: 11,
                backgroundColor: 'rgba(15,23,42,0.75)', color: '#E2E8F0',
              }}
            />
          </Tooltip>
        )}
        {/* Where the clip came from. Shown only for the human-made ones: labelling every
            AI clip "AI labelled" would put a badge on almost every card and say nothing. */}
        {label.creationSource !== 'ai_label' ? (
          <Tooltip title={CREATION_SOURCE_TOOLTIP[label.creationSource]}>
            <Chip
              size="small"
              icon={<ContentCutIcon sx={{ fontSize: 12, color: '#E2E8F0 !important' }} />}
              label={CREATION_SOURCE_LABEL[label.creationSource]}
              sx={{ position: 'absolute', top: 6, right: 6, height: 20, fontSize: 11, backgroundColor: 'rgba(15,23,42,0.75)', color: '#E2E8F0' }}
            />
          </Tooltip>
        ) : label.labelSource === 'human' && (
          <Chip
            size="small"
            icon={<PersonOutlineIcon sx={{ fontSize: 12, color: '#E2E8F0 !important' }} />}
            label="edited"
            sx={{ position: 'absolute', top: 6, right: 6, height: 20, fontSize: 11, backgroundColor: 'rgba(15,23,42,0.75)', color: '#E2E8F0' }}
          />
        )}
        {busy && (
          <Box
            data-testid="card-busy"
            sx={{
              position: 'absolute', inset: 0, display: 'flex', alignItems: 'center',
              justifyContent: 'center', backgroundColor: 'rgba(15,23,42,0.6)',
            }}
          >
            <CircularProgress size={28} sx={{ color: '#F8FAFC' }} />
          </Box>
        )}
      </Box>

      <Box sx={{ p: 1.25, display: 'flex', flexDirection: 'column', gap: 0.75, flexGrow: 1 }}>
        {/* A person's own title when there is one; otherwise the file name and range, so a
            grid of unnamed AI clips isn't a wall of identical product categories. */}
        <Typography variant="body2" sx={{ fontWeight: 700, color: '#1E293B', lineHeight: 1.3 }}>
          {label.name || defaultBrollName(label)}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
          <Typography variant="caption" sx={{ fontWeight: 700, color: '#334155' }}>
            {label.productCategory && label.productCategory !== 'none'
              ? humanize(label.productCategory)
              : 'no product'}
          </Typography>
          {/* The MODEL's confidence in its product guess, so it stops being shown once a
              person has saved the label — at that point what matters is that a human vetted
              it, not how sure the labeler had been. */}
          {label.labelSource === 'human' ? (
            <Chip
              size="small"
              variant="outlined"
              label="human labeled"
              sx={{ height: 18, fontSize: 10, color: '#4F46E5', borderColor: '#4F46E5' }}
            />
          ) : (
            <Chip
              size="small"
              variant="outlined"
              label={confidence}
              sx={{ height: 18, fontSize: 10, color: CONFIDENCE_COLOR[confidence], borderColor: CONFIDENCE_COLOR[confidence] }}
            />
          )}
          {/* The model's Type classification is deliberately not shown: it never described
              anything useful for finding a clip. Why a clip isn't reusable b-roll is still
              said plainly by the disqualifier chips below. */}
        </Box>

        <Typography variant="body2" sx={{ color: '#475569', lineHeight: 1.35 }}>
          {label.description || '—'}
        </Typography>

        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          {/* Human categories first and in their own colour: they're the curation layer,
              and mixing them into the model's chips would hide which is which. */}
          {(label.categories || []).map(slug => (
            <Chip
              key={slug}
              size="small"
              label={categoryName(slug)}
              sx={{ height: 18, fontSize: 10, backgroundColor: '#FEF3C7', color: '#92400E' }}
            />
          ))}
          {/* One field now — Subject and Action merged, so this is no longer two lists
              concatenated with overlapping values. */}
          {label.clipSubject.map(v => (
            <Chip key={v} size="small" label={humanize(v)} sx={{ height: 18, fontSize: 10, backgroundColor: '#EEF2FF', color: '#4F46E5' }} />
          ))}
          {label.keywords.map(k => (
            <Chip key={k} size="small" variant="outlined" label={k} sx={{ height: 18, fontSize: 10 }} />
          ))}
        </Box>

        {label.brollDisqualifiers.length > 0 && (
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {label.brollDisqualifiers.map(reason => (
              <Tooltip key={reason} title={DISQUALIFIER_TOOLTIP[reason]}>
                <Chip
                  size="small"
                  icon={reason === 'talking' ? <RecordVoiceOverIcon sx={{ fontSize: 12 }} /> : undefined}
                  label={DISQUALIFIER_LABEL[reason]}
                  sx={{ height: 18, fontSize: 10, backgroundColor: '#FEF3C7', color: '#92400E' }}
                />
              </Tooltip>
            ))}
          </Box>
        )}
        {label.exclusionReason && (
          <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            “{label.exclusionReason}”
          </Typography>
        )}

        {/* The id an editor pastes into a project to pull this exact clip in. Printed rather than
            hidden behind an icon so it can be read and checked against what was pasted; clicking
            it copies the whole id, since the card is too narrow to show all of a UUID. */}
        <Tooltip title={copied ? 'B-roll ID copied' : `Click to copy · ${label.labelId}`}>
          <Box
            component="button"
            type="button"
            aria-label={`Copy B-roll ID for ${label.name || label.fileName || 'this clip'}`}
            onClick={() => {
              navigator.clipboard?.writeText(label.labelId);
              setCopied(true);
              window.setTimeout(() => setCopied(false), 2000);
            }}
            sx={{
              display: 'flex', alignItems: 'center', gap: 0.5, alignSelf: 'flex-start', maxWidth: '100%',
              p: 0, border: 0, background: 'none', cursor: 'pointer', textAlign: 'left',
              fontFamily: 'monospace', fontSize: 10,
              color: copied ? 'success.main' : 'text.secondary',
              '&:hover': { color: 'primary.main', textDecoration: 'underline' },
            }}
          >
            {copied
              ? <CheckIcon sx={{ fontSize: 12 }} />
              : <ContentCopyIcon sx={{ fontSize: 12 }} />}
            <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {copied ? 'copied' : label.labelId}
            </Box>
          </Box>
        </Tooltip>

        <Box sx={{ flexGrow: 1 }} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ minWidth: 0, flexGrow: 1 }}>
            {/* Only when the title is a person's own name. Otherwise the title already IS the
                file name and range, and repeating it here says the same thing twice. */}
            {label.name && (
              <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                {label.fileName || label.fileId}
              </Typography>
            )}
            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
              {label.planName || 'no project'}
              {label.durationSec != null ? ` · ${formatDuration(label.durationSec)}` : ''}
            </Typography>
          </Box>
          {/* One button. Labels and range are edited together in one modal — they used to be
              two, both writing to the same endpoint. */}
          <Button size="small" startIcon={<EditOutlinedIcon fontSize="small" />} onClick={() => onEdit(label)}>
            Edit
          </Button>
          {/* No tooltips here: MUI renders one as a native `title`, which wins the
              accessible name, so a screen reader would announce the whole sentence
              instead of "Remove". Both labels speak for themselves, and the
              confirmation dialog carries the "nothing is deleted" reassurance. */}
          {label.excludedAt ? (
            <Button size="small" disabled={busy} startIcon={<RestoreFromTrashIcon fontSize="small" />} onClick={() => onRestore(label)}>
              Restore
            </Button>
          ) : (
            <Button size="small" color="error" disabled={busy} startIcon={<DeleteOutlineIcon fontSize="small" />} onClick={() => onRemove(label)}>
              Remove
            </Button>
          )}
        </Box>
      </Box>
    </Paper>
  );
}

/**
 * The B-Roll Library: every labeled clip across every project, filterable by what
 * it shows. Filters compose, so "comforter + b-roll only + hands" narrows to the
 * usable cutaways; the free-text box searches descriptions, notes and tags at once.
 */
export default function BrollLibrary() {
  // Filters, facets, paging and the vocabularies — shared with the video editor's b-roll picker
  // so the two can't drift on how the library is searched.
  const search = useBrollSearch();
  const {
    labels, setLabels, total, loading, page, setPage, pageSize, setPageSize,
    activeFilters,
    facets, vocabulary, categoryVocab, categoryName, createCategory, refreshFacets,
  } = search;
  const runSearch = search.search;

  const [editing, setEditing] = useState<LibraryClipLabel | null>(null);
  // Removal confirms — it changes what everyone else sees in the library — but asks
  // nothing beyond yes/no. The Removed view is the way back.
  const [removing, setRemoving] = useState<LibraryClipLabel | null>(null);
  // Which card has a remove/restore in flight, so it can show progress instead of
  // looking like nothing happened while the request and re-search run.
  const [busyLabelId, setBusyLabelId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [tab, setTab] = useState<BrollTab>('library');
  // Mounted on first visit and kept mounted after that, hidden with display:none. The Add tab
  // holds a loaded folder, a selection and a running transcode poll — and possibly a bulk add
  // in flight — none of which should die because someone glanced at the library. On the
  // library side it keeps the filters, the paging, each card's playback and the scroll
  // position, which re-rendering the tab would throw away.
  const [addVisited, setAddVisited] = useState(false);
  // What the last add did, so the snackbar can offer to go and look at it. `heldBack` counts
  // the ones that landed outside Reusable b-roll — a clip that is mostly sound is filtered out
  // of that view, and being told is the difference between a rule and a clip that vanished.
  const [added, setAdded] = useState<
    { count: number; productCategory: string | null; heldBack: number } | null
  >(null);
  const libraryRef = useRef<HTMLDivElement | null>(null);

  function openTab(next: BrollTab) {
    if (next === 'add') setAddVisited(true);
    // display:none doesn't stop a <video>, so a card left playing would keep talking from
    // behind the other tab.
    if (next !== 'library') {
      libraryRef.current?.querySelectorAll('video').forEach(video => video.pause());
    }
    setTab(next);
  }

  // Re-run the search and the counts after an add, the same pair every other mutation here
  // uses. Note the search is a no-op until a product is chosen — which is exactly why the
  // snackbar below offers to jump to the product the clips were filed under. Without that,
  // a clip you just added is invisible unless the filters already happened to match it.
  const handleAdded = useCallback(async (
    count: number, productCategory: string | null, heldBack: number,
  ) => {
    await runSearch();
    refreshFacets();
    setAdded(current => ({
      count: (current?.count || 0) + count,
      productCategory: productCategory || current?.productCategory || null,
      heldBack: (current?.heldBack || 0) + heldBack,
    }));
  }, [runSearch, refreshFacets]);

  // A failed remove/restore must say so rather than reaching the window as an
  // unhandled rejection, which React's dev overlay turns into a full-page error.
  async function confirmRemove() {
    if (!removing) return;
    const label = removing;
    setRemoving(null);
    setBusyLabelId(label.labelId);
    try {
      await videoEditingService.excludeClipLabel(label.labelId, '');
      await runSearch();
      refreshFacets();
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Could not remove that clip');
    } finally {
      setBusyLabelId(null);
    }
  }

  async function restore(label: LibraryClipLabel) {
    setBusyLabelId(label.labelId);
    try {
      await videoEditingService.restoreClipLabel(label.labelId);
      await runSearch();
      refreshFacets();
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Could not restore that clip');
    } finally {
      setBusyLabelId(null);
    }
  }

  /**
   * Save one clip's labels and, when it moved, its range — in a single request.
   *
   * `ClipLabelEdit` carries inSec/outSec alongside every label field, so the merged Edit modal
   * is one PUT rather than two. Errors propagate so the dialog can show them inline on the form
   * the person is looking at.
   */
  async function saveClipEdit(labelId: string, { edit, rangeMoved }: ClipEditSave) {
    setBusyLabelId(labelId);
    try {
      const { label } = await videoEditingService.updateClipLabel(labelId, edit);
      setLabels(prev => prev.map(l => (l.labelId === label.labelId ? { ...l, ...label } : l)));
      setEditing(prev => (prev && prev.labelId === label.labelId ? { ...prev, ...label } : prev));
      // A moved range means the server regenerated the poster under a fresh signed URL, which a
      // patch of the row can't pick up — re-run the search. A name-only edit doesn't need it.
      if (rangeMoved) await runSearch();
      // Counts move when a label changes category, so refresh them.
      refreshFacets();
    } finally {
      setBusyLabelId(null);
    }
  }

  // Save a range as its OWN library clip, leaving the source untouched — how a long take gets
  // carved into pieces. Every field comes from the dialog, which seeds them from the source.
  async function saveAsNewClip(
    source: LibraryClipLabel,
    range: { inSec: number; outSec: number },
    edit: ClipLabelEdit,
  ) {
    setBusyLabelId(source.labelId);
    try {
      await videoEditingService.createClipLabel({
        ...edit,
        fileId: source.fileId,
        inSec: range.inSec,
        outSec: range.outSec,
        creationSource: 'library_trim',
      });
      await runSearch();
      refreshFacets();
    } finally {
      setBusyLabelId(null);
    }
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 0.5 }}>B-Roll Library</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Reusable b-roll from every project. Talking takes and clips that are more than half
        sound are held back, because they can’t be cut under someone else’s voiceover — switch
        to “Everything” to see them.
      </Typography>

      <Tabs
        value={tab}
        onChange={(_, next: BrollTab) => openTab(next)}
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab value="library" label="Library" />
        <Tab value="add" label="Add clips" />
      </Tabs>

      <Box ref={libraryRef} sx={{ display: tab === 'library' ? 'block' : 'none' }}>
      <ToggleButtonGroup
        size="small"
        exclusive
        value={search.mode}
        onChange={(_, next: LibraryMode | null) => { if (next) search.setMode(next); }}
        sx={{ mb: 2 }}
      >
        <ToggleButton value="broll">Reusable b-roll</ToggleButton>
        <ToggleButton value="all">Everything</ToggleButton>
        <ToggleButton value="removed">Removed</ToggleButton>
      </ToggleButtonGroup>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <BrollFilterBar search={search} />
      </Paper>

      {loading && labels.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : labels.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 6, textAlign: 'center', color: 'text.secondary' }}>
          <Typography variant="body2">
            {(() => {
              if (activeFilters > 0) return 'No clips match those filters.';
              if (search.mode === 'removed') return 'Nothing has been removed from the library.';
              if (search.mode === 'broll') {
                return 'No reusable b-roll yet — every labeled clip so far is a talking take. '
                  + 'Switch to “Everything” to see them.';
              }
              return 'Nothing labeled yet. Run the clip-labeling job on a folder to populate the library.';
            })()}
          </Typography>
        </Paper>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gap: 2,
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(3, 1fr)',
              lg: 'repeat(4, 1fr)',
            },
          }}
        >
          {labels.map(label => (
            <ResultCard
              key={label.labelId}
              label={label}
              busy={busyLabelId === label.labelId}
              categoryName={categoryName}
              onEdit={setEditing}
              onRemove={setRemoving}
              onRestore={restore}
            />
          ))}
        </Box>
      )}

      {total > 0 && (
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, next) => setPage(next)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={e => setPageSize(Number(e.target.value))}
          rowsPerPageOptions={PAGE_SIZES}
          labelRowsPerPage="Clips per page"
        />
      )}
      </Box>

      {addVisited && (
        <Box sx={{ display: tab === 'add' ? 'block' : 'none' }}>
          <AddClipsTab
            vocabulary={vocabulary}
            categories={categoryVocab}
            onCreateCategory={createCategory}
            onAdded={handleAdded}
          />
        </Box>
      )}

      <Dialog open={!!removing} onClose={() => setRemoving(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Remove from the library?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            “{removing?.fileName}” stops appearing in searches. Nothing is deleted — the file
            stays on Drive, and you can put it back from the Removed tab.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoving(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={confirmRemove}>Remove</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!error}
        autoHideDuration={8000}
        onClose={() => setError(null)}
      >
        <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>
      </Snackbar>

      {/* Not just a receipt. The library shows nothing until a product is picked, so a clip
          added under a product the filters don't match is invisible — this is the way to it. */}
      <Snackbar open={!!added} autoHideDuration={12000} onClose={() => setAdded(null)}>
        <Alert
          severity="success"
          onClose={() => setAdded(null)}
          action={added?.productCategory ? (
            <Button
              size="small"
              color="inherit"
              onClick={() => {
                search.setProductCategory(added.productCategory);
                // "Everything", not "Reusable b-roll". A clip that is more than half sound is
                // held back from the b-roll view by design — so sending someone there to look
                // at what they just added is how this button lands on an empty grid and looks
                // like the add silently failed. Everything always contains it, and the card
                // carries the "too loud" chip that explains why it isn't reusable b-roll.
                search.setMode('all');
                openTab('library');
                setAdded(null);
              }}
            >
              Show in library
            </Button>
          ) : undefined}
        >
          Added {added?.count} clip{added?.count === 1 ? '' : 's'} to the library.
          {/* Said plainly, because the alternative is a clip that looks like it never saved.
              The rule is the server's — we only report the verdict it sent back. */}
          {!!added?.heldBack && (
            <Typography variant="caption" sx={{ display: 'block' }}>
              {added.heldBack === added.count
                ? `${added.count === 1 ? 'It has' : 'They have'} too much sound for reusable b-roll, so ${added.count === 1 ? "it's" : "they're"} under “Everything”.`
                : `${added.heldBack} of them have too much sound for reusable b-roll, so they're under “Everything”.`}
            </Typography>
          )}
        </Alert>
      </Snackbar>

      {editing && (
        <ClipEditDialog
          label={editing}
          vocabulary={vocabulary}
          categories={categoryVocab}
          onCreateCategory={createCategory}
          onClose={() => setEditing(null)}
          onSave={saveClipEdit}
          onSaveAsNew={saveAsNewClip}
        />
      )}
    </Box>
  );
}
